import { useRef, useState } from 'react';
import type { DeepgramSettings, Keyterm, WordLibrary } from '@glyph/protocol-ts';
import { useDevice, useEvents, useSaveSettings, useWordLibrary } from '@glyph/core';
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  Chip,
  Field,
  ListRow,
  Note,
  PageHeader,
  Section,
  SegmentedControl,
  Select,
  Slider,
  Stepper,
  SwitchRow,
  TextInput,
  useToast,
} from '@glyph/ui';

export interface SpeechScreenProps {
  deviceId?: string;
}

const MODELS = [
  { value: 'nova-3', label: 'nova-3 · general' },
  { value: 'nova-3-medical', label: 'nova-3 · medical' },
  { value: 'nova-2', label: 'nova-2' },
];
const LANGS = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'en-GB', label: 'English (UK)' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'multi', label: 'Multilingual (code-switching)' },
];
/** Design: Drop / **** / [—]  ->  wordlib blocked_mode drop / replace / blank. */
const BLOCKED_MODES = [
  { value: 'drop' as const, label: 'Drop' },
  { value: 'replace' as const, label: '****' },
  { value: 'blank' as const, label: '[—]' },
];

/** Speech: Deepgram engine, filters + blocked words (word library), event word library with ×1–×3 boost + CSV import, "If Deepgram drops". */
export function SpeechScreen({ deviceId }: SpeechScreenProps) {
  const dev = useDevice(deviceId);
  const s = useSaveSettings(deviceId);
  const wl = useWordLibrary(deviceId);
  const events = useEvents();
  const toast = useToast();
  const [newBlocked, setNewBlocked] = useState('');
  const [newTerm, setNewTerm] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const sp = s.draft?.deepgram;
  const lib = wl.library;
  if (!sp) return <Section />;
  const up = (p: Partial<DeepgramSettings>) => s.update('deepgram', p);
  const dirty = s.isDirty('deepgram');
  const saveLib = async (patch: Partial<WordLibrary>) => {
    if (!lib) return;
    await wl.save({ ...lib, ...patch });
  };
  const blocked = lib?.blocked ?? [];
  const keyterms: Keyterm[] = lib?.keyterms ?? [];
  const addBlocked = () => {
    const w = newBlocked.trim().toLowerCase();
    if (w && !blocked.includes(w)) void saveLib({ blocked: [...blocked, w] });
    setNewBlocked('');
  };
  const addTerm = () => {
    const t = newTerm.trim();
    if (t && !keyterms.some((e) => e.term.toLowerCase() === t.toLowerCase()))
      void saveLib({ keyterms: [...keyterms, { term: t, boost: 2 }] });
    setNewTerm('');
  };
  const importCsv = async (f: File) => {
    const text = await f.text();
    const rows = text
      .split(/\r?\n/)
      .map((l) => l.split(',').map((c) => c.trim().replace(/^"|"$/g, '')))
      .filter((r) => r[0]);
    const next = [...keyterms];
    for (const [term, boost] of rows)
      if (term && !next.some((e) => e.term.toLowerCase() === term.toLowerCase()))
        next.push({ term, boost: Math.min(3, Math.max(1, Number(boost) || 2)) as 1 | 2 | 3 });
    await saveLib({ keyterms: next });
    toast.push(`${next.length - keyterms.length} terms imported`, 'ok');
  };
  const save = async () => {
    await s.save(['deepgram']);
    toast.push('Speech settings saved · session restarts', 'ok');
  };

  const engineSwitches = (
    <>
      <SwitchRow
        title="Interim results"
        hint="paint words before the phrase is final"
        checked={sp.interim_results}
        onChange={(v) => up({ interim_results: v })}
      />
      <SwitchRow
        title="Smart format"
        hint="dates, currency, phone numbers"
        checked={sp.smart_format}
        onChange={(v) => up({ smart_format: v })}
      />
      <SwitchRow
        title="Punctuation"
        hint="sentence punctuation and casing"
        checked={sp.punctuate}
        onChange={(v) => up({ punctuate: v })}
      />
      <SwitchRow
        title="Numerals"
        hint="“twelve” → “12”"
        checked={sp.numerals}
        onChange={(v) => up({ numerals: v })}
      />
      <SwitchRow
        title="Speaker labels"
        hint="diarization · adds ~200 ms"
        checked={sp.diarize}
        onChange={(v) => up({ diarize: v })}
      />
    </>
  );
  const replaceSeg = (fw?: boolean) => (
    <SegmentedControl
      fullWidth={fw}
      value={lib?.blocked_mode ?? 'drop'}
      onChange={(v) => void saveLib({ blocked_mode: v })}
      options={BLOCKED_MODES}
    />
  );
  const chips = (
    <div className="flex flex-wrap gap-1.5">
      {blocked.map((w) => (
        <Chip key={w} onRemove={() => void saveLib({ blocked: blocked.filter((x) => x !== w) })}>
          {w}
        </Chip>
      ))}
    </div>
  );
  const blockedInput = (
    <div className="flex gap-2">
      <TextInput
        value={newBlocked}
        onChange={(e) => setNewBlocked(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && addBlocked()}
        placeholder="Add word or phrase"
        className="flex-1"
      />
      <Button size="responsive" onClick={addBlocked}>
        Add
      </Button>
    </div>
  );
  const termRows = keyterms.map((t, i) => (
    <div
      key={t.term}
      className="flex min-h-12 items-center justify-between gap-3 border-t border-line px-4 py-2.5 md:min-h-0 md:px-0 md:py-2"
    >
      <span className="text-lg font-normal text-fg md:text-md">{t.term}</span>
      <Stepper
        value={t.boost}
        min={1}
        max={3}
        format={(x) => `×${x}`}
        onChange={(b) =>
          void saveLib({ keyterms: keyterms.map((e, j) => (j === i ? { ...e, boost: b as 1 | 2 | 3 } : e)) })
        }
        onRemove={() => void saveLib({ keyterms: keyterms.filter((_, j) => j !== i) })}
      />
    </div>
  ));
  const termInput = (
    <div className="flex gap-2">
      <TextInput
        value={newTerm}
        onChange={(e) => setNewTerm(e.target.value)}
        onKeyDown={(e) => e.key === 'Enter' && addTerm()}
        placeholder="Product, name, acronym"
        className="flex-1"
      />
      <Button size="responsive" onClick={addTerm}>
        Add
      </Button>
      <Button size="responsive" onClick={() => fileRef.current?.click()}>
        Import CSV
      </Button>
      <input
        ref={fileRef}
        type="file"
        accept=".csv,text/csv"
        hidden
        onChange={(e) => e.target.files?.[0] && void importCsv(e.target.files[0])}
      />
    </div>
  );
  const eventList = Array.isArray(events.data) ? events.data : [];
  const eventOptions = eventList.map((e) => ({ value: e.name, label: e.name }));
  const eventSelect = (block: boolean) => (
    <Select
      block={block}
      value={lib?.name ?? ''}
      onChange={(v) => void saveLib({ name: v, event_id: eventList.find((e) => e.name === v)?.id ?? null })}
      options={eventOptions.length ? eventOptions : [{ value: lib?.name ?? '', label: lib?.name ?? '—' }]}
      aria-label="Event"
    />
  );
  const budget = wl.budget;
  const steps = [
    ['01', 'Hold last line on screen', 'no blanking · reconnect with back-off 0.5 → 4 s'],
    [
      '02',
      'Replay the audio ring on reconnect',
      'last 10 s re-sent at up to 4× · overlap deduped by sample position',
    ],
    [
      '03',
      'Alert OLED + cloud · partner follow if paired',
      'audio keeps buffering so the recording has no gap',
    ],
  ];

  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Speech recognition"
        meta={`Deepgram streaming · ${dev.data?.name ?? ''} · session restarts on engine change only`}
        actions={
          <>
            {dirty && <Button onClick={() => s.revert('deepgram')}>Revert</Button>}
            <Button variant="primary" disabled={!dirty || s.isSaving} onClick={() => void save()}>
              {dirty ? 'Save' : 'Saved'}
            </Button>
          </>
        }
      />

      {/* mobile */}
      <div className="flex flex-col gap-4 md:hidden">
        <Card>
          <CardHeader title="Engine" meta={sp.model.toUpperCase()} />
          <ListRow
            title="Model"
            trailing={<Select value={sp.model} onChange={(v) => up({ model: v })} options={MODELS} />}
          />
          <ListRow
            title="Language"
            trailing={<Select value={sp.language} onChange={(v) => up({ language: v })} options={LANGS} />}
          />
          <Slider
            row
            label="Endpointing"
            value={sp.endpointing}
            min={100}
            max={1000}
            step={50}
            unit="ms"
            onChange={(v) => up({ endpointing: v })}
          />
          {engineSwitches}
        </Card>
        <Card>
          <CardHeader title="Filters" />
          <SwitchRow
            title="Profanity filter"
            hint="Deepgram built-in list · masks as ****"
            checked={sp.profanity_filter}
            onChange={(v) => up({ profanity_filter: v })}
          />
          <div className="flex flex-col gap-2.5 border-t border-line px-4 pb-3.5 pt-3">
            <span className="text-lg text-fg">Blocked words · handling</span>
            {replaceSeg(true)}
          </div>
          <div className="px-4 pb-3 pt-1">{chips}</div>
          <div className="px-4 pb-4">{blockedInput}</div>
        </Card>
        <Card>
          <CardHeader title="Event word library" meta={`${keyterms.length} TERMS`} />
          <ListRow title="Event" trailing={eventSelect(false)} />
          {termRows}
          <div className="border-t border-line px-4 py-3.5">{termInput}</div>
        </Card>
        <Card>
          <CardHeader title="If Deepgram drops" />
          {steps.map(([n, t, h]) => (
            <ListRow key={n} label={n} title={t} sub={h} />
          ))}
        </Card>
        <Button size="lg" variant="primary" disabled={!dirty || s.isSaving} onClick={() => void save()}>
          {dirty ? 'Save speech settings' : 'Saved'}
        </Button>
      </div>

      {/* desktop */}
      <div
        className="hidden items-start gap-5 md:grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}
      >
        <Card mode="padded">
          <CardTitle>Engine</CardTitle>
          <Field label="Model">
            <Select block value={sp.model} onChange={(v) => up({ model: v })} options={MODELS} />
          </Field>
          <Field label="Language">
            <Select block value={sp.language} onChange={(v) => up({ language: v })} options={LANGS} />
          </Field>
          <Slider
            label="Endpointing"
            value={sp.endpointing}
            min={100}
            max={1000}
            step={50}
            unit="ms"
            onChange={(v) => up({ endpointing: v })}
          />
          <div className="flex flex-col">{engineSwitches}</div>
        </Card>
        <Card mode="padded">
          <CardTitle>Filters</CardTitle>
          <SwitchRow
            first
            title="Profanity filter"
            hint="Deepgram built-in list · masks as ****"
            checked={sp.profanity_filter}
            onChange={(v) => up({ profanity_filter: v })}
            className="border-t-0 py-0"
          />
          <Field label="Blocked words · handling" className="border-t border-line pt-4">
            {replaceSeg()}
          </Field>
          {chips}
          {blockedInput}
        </Card>
        <Card mode="padded">
          <CardTitle
            meta={`${keyterms.length} TERMS${budget ? ` · ${budget.used}/${budget.total} TOKENS` : ''}`}
          >
            Event word library
          </CardTitle>
          <Field label="Event">{eventSelect(true)}</Field>
          <div className="flex flex-col">{termRows}</div>
          {termInput}
        </Card>
        <Card mode="padded">
          <CardTitle>If Deepgram drops</CardTitle>
          <div className="flex flex-col">
            {steps.map(([n, t, h]) => (
              <div key={n} className="grid grid-cols-[28px_minmax(0,1fr)] gap-3 border-t border-line py-2.5">
                <span className="font-mono text-sm font-medium text-fg4">{n}</span>
                <div className="flex flex-col gap-[3px]">
                  <span className="text-base font-medium">{t}</span>
                  <span className="font-mono text-xs text-fg4">{h}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Section>
  );
}
